import { storeToRefs } from 'pinia'
import { useDeviceStore } from '@/stores'
import { defaultReportError } from '@/composables/useErrorLogger'

import type { DksSlotInfo, keyMapItem, MtSlotInfo, TglSlotInfo } from '@rapoo/device-sdk'

export const useKeySDK = () => {
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const getKeyFeature = () => {
    const feature = currentDevice.value?.features.keyboardKeys
    if (!feature) {
      console.warn('Current device does not support keyboard remap')
      return undefined
    }

    return feature
  }

  const getDksList = async () => {
    const feature = getKeyFeature()
    if (!feature) return undefined
    try {
      return await feature.getDksList()
    } catch (error) {
      console.error('Failed to get keys dks list', error)
      defaultReportError(`Failed to get keys dks list: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const getDksByIndex = async (index: number) => {
    const feature = getKeyFeature()
    if (!feature) return undefined
    try {
      return await feature.getDksByIndex(index)
    } catch (error) {
      console.error(`Failed to get dks by index ${index}`, error)
      defaultReportError(`Failed to get dks by index ${index}: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setDksByIndex = async (dks: DksSlotInfo, index: number) => {
    const feature = getKeyFeature()
    if (!feature) return
    try {
      await feature.setDksByIndex(dks, index)
    } catch (error) {
      console.error(`Failed to set dks by index ${index}`, error)
      defaultReportError(`Failed to set dks by index ${index}: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getMtList = async () => {
    const feature = getKeyFeature()
    if (!feature) return undefined
    try {
      return await feature.getMtList()
    } catch (error) {
      console.error('Failed to get keys mt list', error)
      defaultReportError(`Failed to get keys mt list: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const getMtByIndex = async (index: number) => {
    const feature = getKeyFeature()
    if (!feature) return undefined
    try {
      return await feature.getMtByIndex(index)
    } catch (error) {
      console.error(`Failed to get mt by index ${index}`, error)
      defaultReportError(`Failed to get mt by index ${index}: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setMtByIndex = async (mt: MtSlotInfo, index: number) => {
    const feature = getKeyFeature()
    if (!feature) return
    try {
      await feature.setMtByIndex(mt, index)
    } catch (error) {
      console.error(`Failed to set mt by index ${index}`, error)
      defaultReportError(`Failed to set mt by index ${index}: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const getTglList = async () => {
    const feature = getKeyFeature()
    if (!feature) return undefined
    try {
      return await feature.getTglList()
    } catch (error) {
      console.error('Failed to get keys tgl list', error)
      defaultReportError(`Failed to get keys tgl list: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const getTglByIndex = async (index: number) => {
    const feature = getKeyFeature()
    if (!feature) return undefined
    try {
      return await feature.getTglByIndex(index)
    } catch (error) {
      console.error(`Failed to get tgl by index ${index}`, error)
      defaultReportError(`Failed to get tgl by index ${index}: ${JSON.stringify(error)}`)
      return undefined
    }
  }

  const setTglByIndex = async (tgl: TglSlotInfo, index: number) => {
    const feature = getKeyFeature()
    if (!feature) return
    try {
      await feature.setTglByIndex(tgl, index)
    } catch (error) {
      console.error(`Failed to set tgl by index ${index}`, error)
      defaultReportError(`Failed to set tgl by index ${index}: ${JSON.stringify(error)}`)
      throw error
    }
  }

  const setLayerKeysMap = async (item: keyMapItem, isFn?: boolean) => {
    const feature = getKeyFeature()
    if (!feature) return
    try {
      await feature.setLayerKeysMap(item, isFn)
    } catch (error) {
      console.error('Failed to set layer keys map', error)
      defaultReportError(`Failed to set layer keys map: ${JSON.stringify(error)}`)
      throw error
    }
  }

  return {
    getKeyFeature,
    getDksList,
    getDksByIndex,
    setDksByIndex,
    getMtList,
    getMtByIndex,
    setMtByIndex,
    getTglList,
    getTglByIndex,
    setTglByIndex,
    setLayerKeysMap,
  }
}
